import { getHomeComputerProjectTarget } from '#pplx/frontend/apps/ask/ask-composer/getHomeComputerProjectTarget.js';
import { useAskComposerSearchModeSelected } from '#pplx/frontend/apps/ask/ask-composer/useAskComposerSearchModeSelected.js';
import { useAskInput } from '#pplx/frontend/apps/ask/ask-input/ask-input-hooks/useAskInput.js';
import { useAskDeepResearchModelSelectorOverrides } from '#pplx/frontend/apps/ask/ask-input/effort/useAskDeepResearchModelSelectorOverrides.js';
import { useComputerEffortState } from '#pplx/frontend/apps/ask/ask-input/effort/useComputerEffortState.js';
import { useAskComposerAttachments } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/useAskComposerAttachments.js';
import { useAskComposerCloudMenuProps } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/useAskComposerCloudMenuProps.js';
import { buildComposerSubmitParams, resolveComposerValue, } from '#pplx/frontend/apps/ask/composer-ui/buildComposerSubmitParams.js';
import { getSubmissionPolicy } from '#pplx/frontend/apps/ask/composer-ui/getSubmissionPolicy.js';
import { useComposerUploadSubmission } from '#pplx/frontend/apps/ask/composer-ui/useComposerUploadSubmission.js';
import { useComputerMissingAttachmentGuard } from '#pplx/frontend/apps/ask/computer/missing-attachment/useComputerMissingAttachmentGuard.js';
import { useComputerRemoteBoxExecutionTargetAddon } from '#pplx/frontend/apps/ask/computer/remotebox-execution-target/useComputerRemoteBoxExecutionTargetAddon.js';
import { isAskDeepResearchInput } from '#pplx/frontend/apps/ask/hooks/ask-deep-research-hooks/deepResearchInput.js';
import { useBrowserAgentMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useBrowserAgentMode.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useResultsSelector } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useLocalUserSettings } from '#pplx/frontend/apps/ask/user-settings/getLocalUserSettings.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useClientRender } from '#pplx/frontend/common/hooks/useClientRender.js';
import { MAX_CHARACTERS_ALLOWED_TO_SUBMIT } from '#pplx/frontend/components/utils/consts.js';
import { createPlainTextComposerValue } from '#pplx/frontend/composer-sdk/createPlainTextComposerValue.js';
import { serializedStateToMarkdown } from '#pplx/frontend/composer-sdk/serializedStateToMarkdown.js';
import { useCallback, useMemo } from 'react';
/** Acceptance and navigation remain in the existing Ask controller. */
export function useAskComposerAdapter({ surfaceConfig, onAcceptedSubmit, onBeforeSubmit, }) {
    const selectedSearchMode = useSelectedSearchMode();
    const { canClearSearchMode } = useBrowserAgentMode();
    const backendUUID = useResultsSelector((state) => state.lastResult?.backend_uuid);
    const handleSearchModeSelected = useAskComposerSearchModeSelected({
        surface: surfaceConfig.querySource,
    });
    const { askDeepResearchModel, compareModels, configuredModel, initialConfiguredModelIsLoading, sources, } = useThreadConfig();
    const [project, setSelectedProject] = useLocalUserSettings('homeComputerProjectSelection', null);
    const { collection, targetThreadAccessLevel } = getHomeComputerProjectTarget({
        selectedProject: import.meta.env.VITE_IS_SIDECAR_BUILD ? null : project,
        selectedSearchMode,
    });
    const isComputer = selectedSearchMode === SearchMode.ASI;
    const { plugin: remoteBoxPlugin, workflowConfig: remoteBoxWorkflowConfig } = useComputerRemoteBoxExecutionTargetAddon({
        eligible: !import.meta.env.VITE_IS_SIDECAR_BUILD && isComputer,
        mode: { mode: 'new-task', projectUuid: collection?.uuid },
    });
    const { computerEffortPreferences, computerEffortEnabled, handleComputerEffortPreferencesChange, isComputerModeHydrating, modeConfig, } = useComputerEffortState({
        isFollowUp: false,
        reason: surfaceConfig.reason,
        searchModel: configuredModel,
        selectedSearchMode,
    });
    const hasMounted = useClientRender();
    const isPolicyBlocked = !hasMounted || initialConfiguredModelIsLoading || isComputerModeHydrating;
    const [value, json, actions] = useAskInput({
        collection,
        workflowConfig: remoteBoxWorkflowConfig,
        csrRedirect: true,
        disabled: isPolicyBlocked,
        modeConfig,
        onAcceptedSubmit,
        reason: surfaceConfig.reason,
        targetThreadAccessLevel,
    });
    const modelSelectorOverrides = useAskDeepResearchModelSelectorOverrides({
        enabled: isAskDeepResearchInput({ askDeepResearchModel, searchMode: selectedSearchMode, json }),
    });
    const attachments = useAskComposerAttachments({
        backendUUID,
        characterLimit: MAX_CHARACTERS_ALLOWED_TO_SUBMIT,
        isFollowUp: false,
        inputLength: value.length,
        querySource: surfaceConfig.querySource,
        searchModel: configuredModel,
        selectedSearchMode,
        showFileUpload: true,
    });
    const cloudMenuProps = useAskComposerCloudMenuProps({
        attachments,
        showFileUpload: true,
        disabled: false,
        isFollowUp: false,
    });
    const { submitUploads } = attachments;
    const modeControls = useMemo(() => ({
        canClearSearchMode,
        computerEffort: {
            computerEffortPreferences,
            computerEffortEnabled,
            onComputerEffortPreferencesChange: handleComputerEffortPreferencesChange,
        },
        modelSelectorOverrides,
        onSearchModeSelected: handleSearchModeSelected,
        onSearchModeCleared: handleSearchModeSelected,
        selectedSearchMode,
    }), [
        canClearSearchMode,
        computerEffortPreferences,
        computerEffortEnabled,
        handleComputerEffortPreferencesChange,
        handleSearchModeSelected,
        modelSelectorOverrides,
        selectedSearchMode,
    ]);
    const composerValue = useMemo(() => resolveComposerValue(value, json), [json, value]);
    const onValueChange = useCallback((nextValue) => {
        actions.onChange(serializedStateToMarkdown(nextValue), nextValue);
    }, [actions]);
    const handleSubmit = useCallback((submission, resources) => {
        onBeforeSubmit?.();
        return actions.onSubmit({
            ...buildComposerSubmitParams({
                submission,
                ...resources,
                querySource: surfaceConfig.querySource,
            }),
            ...(submission.attempt && { attempt: submission.attempt }),
            promptSource: 'user',
            sourcesOverride: sources,
            ...submission.submitParams,
            // The dispatch snapshot replaces stale tabs from the suggestion or editor.
            mentions: resources?.mentions,
        });
    }, [actions, onBeforeSubmit, sources, surfaceConfig.querySource]);
    const submissionPolicyInputs = useMemo(() => ({
        disableSubmission: isPolicyBlocked,
        hasFailedSelectedFile: submitUploads.some((upload) => upload.status === 'failed'),
    }), [isPolicyBlocked, submitUploads]);
    const isSubmissionBlocked = useCallback((submission) => getSubmissionPolicy({
        ...submissionPolicyInputs,
        draftLength: serializedStateToMarkdown(submission.state).length,
    }), [submissionPolicyInputs]);
    const characterCount = value.length;
    const disableSubmission = getSubmissionPolicy({
        ...submissionPolicyInputs,
        draftLength: characterCount,
    });
    const { isSubmitDisabled, isSubmitQueued, onSubmit, requestSubmit } = useComposerUploadSubmission({
        analyticsSurface: surfaceConfig.analyticsSurface,
        attachments: {
            ...attachments,
            // Home's selected mode includes the Sidecar-supported-mode fallback.
            resolvedSearchMode: selectedSearchMode,
        },
        compareModelCount: compareModels.length,
        disableSubmission,
        getSubmissionMentions: (submission) => submission.submitParams?.mentions,
        isFollowUp: false,
        isSubmissionBlocked,
        onSubmit: handleSubmit,
        querySource: surfaceConfig.querySource,
        showStopButton: false,
        submissionRevision: composerValue,
    });
    const status = isSubmitQueued ? 'submitting' : 'idle';
    const missingAttachment = useComputerMissingAttachmentGuard({
        isComputer,
        query: value,
        attachmentCount: submitUploads.length,
        inProject: collection !== null,
        onSubmit,
    });
    const onDictationSubmit = useCallback((submission) => requestSubmit({
        ...submission,
        attempt: { method: 'dictation', fromQueue: false },
    }) !== false, [requestSubmit]);
    const handleSuggestionSubmit = useCallback((params) => {
        if (!params.query?.trim())
            return false;
        const submission = {
            state: createPlainTextComposerValue(params.query),
            plainText: params.query,
            submitParams: params,
        };
        return requestSubmit(submission);
    }, [requestSubmit]);
    return useMemo(() => ({
        value: composerValue,
        onValueChange,
        onSubmit: missingAttachment.onSubmit,
        onDictationSubmit,
        missingAttachmentGuard: missingAttachment.element,
        isSubmitDisabled,
        status,
        characterCount,
        modeControls,
        attachments: { ...attachments, cloudMenuProps },
        remoteBoxPlugin,
        autosuggestControls: {
            attachments: attachments.attachmentUrls,
            handleSubmit: handleSuggestionSubmit,
            inputJson: composerValue,
            inputQuery: value,
            onValueChange,
            selectedSearchMode,
            setUserInput: actions.setUserInput,
            sources,
        },
        isVisible: isComputer,
        onProjectChange: setSelectedProject,
        project,
    }), [
        composerValue,
        onValueChange,
        missingAttachment.onSubmit,
        onDictationSubmit,
        missingAttachment.element,
        isSubmitDisabled,
        status,
        characterCount,
        modeControls,
        attachments,
        cloudMenuProps,
        remoteBoxPlugin,
        handleSuggestionSubmit,
        value,
        selectedSearchMode,
        actions.setUserInput,
        sources,
        isComputer,
        setSelectedProject,
        project,
    ]);
}
//# sourceMappingURL=askComposerAdapter.js.map